/* eslint-disable prefer-const */
import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';
import RichTextEditor from '../editor/RichTextEditor';

interface Blog {
  _id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featuredImage?: string;
  category: string;
  tags: string[];
  author: {
    name: string;
    email: string;
    avatar?: string;
  };
  status: 'draft' | 'published' | 'archived';
  isFeatured: boolean;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  readTime: number;
  publishedAt?: string;
  isDeleted?: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

const BlogPage: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [itemsPerPage] = useState(10);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
  const [blogToDelete, setBlogToDelete] = useState<string | null>(null);
  const [blogLoading, setBlogLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Form state for adding new blog
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    tags: '',
    authorName: '',
    authorEmail: '',
    readTime: 1,
    status: 'draft' as 'draft' | 'published' | 'archived',
    isFeatured: false,
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Rich text editor state (now handled by CKEditor component)

  useEffect(() => {
    // Test API connectivity first
    console.log('🔧 Testing API connectivity...');
    console.log(
      '🔧 API Base URL:',
      process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'
    );
    console.log('🔧 Blog List Endpoint:', API_ENDPOINTS.BLOG.LIST);
    console.log('🔧 Current Page:', currentPage);
    console.log('🔧 Items Per Page:', itemsPerPage);
    console.log('🔧 Show Deleted:', showDeleted);

    fetchBlogs();
  }, [currentPage, showDeleted]);

  // Add CSS styles for blog content rendering
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .blog-content h1, .blog-content h2, .blog-content h3, 
      .blog-content h4, .blog-content h5, .blog-content h6 {
        color: #ede8c5;
        margin-top: 1rem;
        margin-bottom: 0.5rem;
        font-weight: bold;
      }
      .blog-content h1 { font-size: 1.5rem; }
      .blog-content h2 { font-size: 1.25rem; }
      .blog-content h3 { font-size: 1.125rem; }
      .blog-content p {
        margin-bottom: 1rem;
        color: #ede8c5;
      }
      .blog-content ul, .blog-content ol {
        margin-bottom: 1rem;
        padding-left: 1.5rem;
        color: #ede8c5;
      }
      .blog-content li {
        margin-bottom: 0.25rem;
        color: #ede8c5;
      }
      .blog-content strong {
        font-weight: bold;
        color: #ede8c5;
      }
      .blog-content code {
        background-color: #465705;
        color: #ede8c5;
        padding: 0.125rem 0.25rem;
        border-radius: 0.25rem;
        font-family: monospace;
      }
      .blog-content pre {
        background-color: #465705;
        color: #ede8c5;
        padding: 1rem;
        border-radius: 0.5rem;
        overflow-x: auto;
        margin-bottom: 1rem;
      }
      .blog-content blockquote {
        border-left: 4px solid #bbe425;
        padding-left: 1rem;
        margin: 1rem 0;
        color: #ede8c5;
        font-style: italic;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // CKEditor handles all rich text functionality

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      let apiUrl;

      if (showDeleted) {
        // Use dedicated deleted blogs endpoint
        apiUrl = `${API_ENDPOINTS.BLOG.LIST.replace('/blogs', '/blogs/admin/deleted')}?page=${currentPage}&limit=${itemsPerPage}`;
        console.log('🔄 Fetching DELETED blogs from:', apiUrl);
      } else {
        // Use regular blogs endpoint for active blogs
        apiUrl = `${API_ENDPOINTS.BLOG.LIST}?page=${currentPage}&limit=${itemsPerPage}`;
        console.log('🔄 Fetching ACTIVE blogs from:', apiUrl);
      }

      console.log('🔄 Show Deleted Filter:', showDeleted);
      const response = await services.get(apiUrl);
      console.log('📊 Blogs API Response:', response);

      if (response.data.success) {
        // Handle different response structures for active vs deleted blogs
        let blogsData, paginationData;

        if (showDeleted) {
          // For deleted blogs, data is nested under data.blogs and pagination under data.pagination
          blogsData = response.data.data?.blogs || [];
          paginationData = response.data.data?.pagination || {};
          console.log('📝 Deleted blogs data structure:', {
            blogs: blogsData,
            pagination: paginationData,
          });
        } else {
          // For active blogs, data is directly under data.data and pagination under data.pagination
          blogsData = response.data.data || [];
          paginationData = response.data.pagination || {};
          console.log('📝 Active blogs data structure:', {
            blogs: blogsData,
            pagination: paginationData,
          });
        }

        console.log('📝 Raw blogs data:', blogsData);
        console.log('📝 Raw blogs data type:', typeof blogsData);
        console.log('📝 Is blogsData array?', Array.isArray(blogsData));
        console.log('📊 API Response totals:', {
          total: paginationData.total,
          pages: paginationData.pages,
          page: paginationData.page,
          limit: paginationData.limit,
          dataLength: Array.isArray(blogsData) ? blogsData.length : 'Not an array',
        });

        // Ensure blogsData is an array before filtering
        const blogsArray = Array.isArray(blogsData) ? blogsData : [];
        console.log('📝 Blogs array:', blogsArray);

        // Ensure all blogs have required properties and filter out any null/undefined entries
        const validBlogs = blogsArray.filter((blog) => blog && blog._id);
        console.log('✅ Valid blogs:', validBlogs);
        setBlogs(validBlogs);

        // Extract pagination info from the correct structure
        const totalPagesValue = paginationData.pages || 1;
        const currentPageValue = paginationData.page || 1;
        const totalCountValue = paginationData.total || validBlogs.length;

        console.log('📊 Setting total pages to:', totalPagesValue);
        console.log('📊 Setting current page to:', currentPageValue);
        console.log('📊 Setting total blogs to:', totalCountValue);

        setTotalPages(totalPagesValue);
        setCurrentPage(currentPageValue);
        setTotalBlogs(totalCountValue);
        setError(null); // Clear any previous errors
      } else {
        console.error('❌ API returned success: false:', response.data);
        setError('Failed to fetch blogs');
      }
    } catch (err: unknown) {
      console.error('❌ Error fetching blogs:', err);
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to fetch blogs';
      console.error('❌ Error message:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogDetails = async (blogId: string) => {
    try {
      setBlogLoading(true);
      const response = await services.get(API_ENDPOINTS.BLOG.GET_BY_ID(blogId));

      if (response.data.success) {
        console.log('📖 Blog details fetched:', response.data.data);
        console.log('📖 Author data in details:', {
          author: response.data.data.author,
          hasAuthor: !!response.data.data.author,
          authorName: response.data.data.author?.name,
          authorEmail: response.data.data.author?.email,
        });
        setSelectedBlog(response.data.data);
        setShowViewModal(true);
      } else {
        setError('Failed to fetch blog details');
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch blog details');
    } finally {
      setBlogLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // CKEditor handles all rich text functionality

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('📝 Form submitted! Starting validation...');
    console.log('📝 Current form data:', formData);

    // Let backend handle all validation
    console.log('🚀 Proceeding with API call - backend will handle validation...');

    // Determine if we're creating or updating
    const isEditing = showEditModal && selectedBlog;

    try {
      setSubmitting(true);
      setError(null); // Clear any previous errors

      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title || 'Untitled');

      // Generate proper slug from title if slug is empty or invalid
      let slug = formData.slug;
      if (!slug || slug.trim() === '') {
        slug = (formData.title || 'untitled')
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-+|-+$/g, '');
      }
      formDataToSend.append('slug', slug || 'untitled');

      formDataToSend.append('excerpt', formData.excerpt || 'No excerpt');
      formDataToSend.append('content', formData.content || '<p>No content</p>');
      formDataToSend.append('category', formData.category || 'General');

      // Convert tags string to array
      const tagsArray = (formData.tags || 'general')
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
      if (tagsArray.length === 0) {
        tagsArray.push('general');
      }
      tagsArray.forEach((tag) => formDataToSend.append('tags[]', tag));

      // Send author data in multiple formats for backend compatibility
      const authorData = {
        name: formData.authorName || 'Unknown Author',
        email: formData.authorEmail || 'unknown@example.com',
      };

      // Format 1: Nested object format
      formDataToSend.append('author[name]', authorData.name);
      formDataToSend.append('author[email]', authorData.email);

      // Format 2: Separate fields
      formDataToSend.append('authorName', authorData.name);
      formDataToSend.append('authorEmail', authorData.email);

      // Format 3: JSON string (for some backends)
      formDataToSend.append('author', JSON.stringify(authorData));

      formDataToSend.append('readTime', formData.readTime.toString());
      formDataToSend.append('status', formData.status);
      formDataToSend.append('isFeatured', formData.isFeatured.toString());

      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }

      console.log('🚀 Submitting blog with data:', {
        title: formData.title,
        excerpt: formData.excerpt,
        content: formData.content.substring(0, 100) + '...',
        category: formData.category,
        tags: tagsArray,
        author: { name: formData.authorName, email: formData.authorEmail },
        readTime: formData.readTime,
        status: formData.status,
        isFeatured: formData.isFeatured,
      });

      console.log('🌐 API Endpoint:', API_ENDPOINTS.BLOG.CREATE);

      // Debug FormData contents
      console.log('📋 FormData contents:');
      const formDataEntries = Array.from(formDataToSend.entries());
      for (const [key, value] of formDataEntries) {
        console.log(`${key}:`, value);
      }

      // Specifically log author data being sent
      console.log('👤 Author data being sent:', {
        authorData,
        authorName: formData.authorName,
        authorEmail: formData.authorEmail,
        hasName: !!formData.authorName,
        hasEmail: !!formData.authorEmail,
        nameLength: formData.authorName?.length || 0,
        emailLength: formData.authorEmail?.length || 0,
      });

      const apiEndpoint = isEditing
        ? API_ENDPOINTS.BLOG.UPDATE(selectedBlog._id)
        : API_ENDPOINTS.BLOG.CREATE;
      const apiMethod = isEditing ? services.put : services.post;

      console.log('🔄 API Operation:', isEditing ? 'UPDATE' : 'CREATE');
      console.log('🌐 API Endpoint:', apiEndpoint);

      let response;
      if (isEditing) {
        // For updates, check if there's a new image
        if (selectedImage) {
          // If there's a new image, use FormData to handle file upload
          console.log('📝 Update with new image - using FormData');
          response = await apiMethod(apiEndpoint, formDataToSend);
        } else {
          // If no new image, try JSON format first, then fallback to FormData
          console.log('📝 Update without new image - trying JSON format first');

          const updateData = {
            title: formData.title || 'Untitled',
            slug: slug || 'untitled',
            excerpt: formData.excerpt || 'No excerpt',
            content: formData.content || '<p>No content</p>',
            category: formData.category || 'General',
            tags: tagsArray,
            author: {
              name: formData.authorName || 'Unknown Author',
              email: formData.authorEmail || 'unknown@example.com',
            },
            readTime: formData.readTime || 1,
            status: formData.status || 'draft',
            isFeatured: formData.isFeatured || false,
          };

          console.log('📝 JSON update data:', updateData);
          console.log('📝 Author data in JSON:', {
            author: updateData.author,
            authorName: formData.authorName,
            authorEmail: formData.authorEmail,
          });

          try {
            response = await apiMethod(apiEndpoint, updateData);
            console.log('✅ JSON update successful');
          } catch (jsonError) {
            console.log('❌ JSON update failed, trying FormData:', jsonError);
            response = await apiMethod(apiEndpoint, formDataToSend);
            console.log('✅ FormData update successful');
          }
        }
      } else {
        // For create, use FormData
        console.log('📝 Using FormData for create operation');
        response = await apiMethod(apiEndpoint, formDataToSend);
      }

      console.log('✅ API Response:', response);
      console.log('✅ Response data:', response.data);
      if (response.data && response.data.data) {
        console.log('✅ Updated blog data:', response.data.data);
        console.log('✅ Updated author data:', {
          author: response.data.data.author,
          hasAuthor: !!response.data.data.author,
          authorName: response.data.data.author?.name,
          authorEmail: response.data.data.author?.email,
          authorType: typeof response.data.data.author,
        });

        // Compare with what we sent
        console.log('🔄 Author data comparison:', {
          sent: {
            name: formData.authorName,
            email: formData.authorEmail,
          },
          received: {
            name: response.data.data.author?.name,
            email: response.data.data.author?.email,
          },
          match: {
            name: formData.authorName === response.data.data.author?.name,
            email: formData.authorEmail === response.data.data.author?.email,
          },
        });
      }

      if (response.data && response.data.success) {
        console.log('✅ Blog operation successful, refreshing list...');
        if (isEditing) {
          setShowEditModal(false);
        } else {
          setShowAddModal(false);
        }
        resetForm();

        // Try to refresh the blog list
        try {
          await fetchBlogs();
          console.log('✅ Blog list refreshed successfully');
        } catch (refreshError) {
          console.error('❌ Failed to refresh blog list:', refreshError);
          // Don't show error to user since the blog was created successfully
          // Just log the error for debugging
          // Clear any error state that might have been set
          setError(null);
        }
      } else {
        setError(response.data?.message || `Failed to ${isEditing ? 'update' : 'create'} blog`);
      }
    } catch (err: unknown) {
      console.error('❌ Error creating blog:', err);
      const error = err as { data?: { message?: string }; status?: number; statusText?: string };
      console.error('❌ Error details:', {
        status: error.status,
        statusText: error.statusText,
        data: error.data,
      });
      setError(
        error.data?.message ||
          error.statusText ||
          `Failed to ${isEditing ? 'update' : 'create'} blog`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: '',
      tags: '',
      authorName: '',
      authorEmail: '',
      readTime: 1,
      status: 'draft',
      isFeatured: false,
    });
    setSelectedImage(null);
    setImagePreview(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string, isDeleted?: boolean) => {
    if (isDeleted) {
      return { bg: '#dc2626', text: '#ffffff' }; // Red for deleted
    }
    switch (status) {
      case 'published':
        return { bg: '#10b981', text: '#ffffff' };
      case 'draft':
        return { bg: '#f59e0b', text: '#ffffff' };
      case 'archived':
        return { bg: '#6b7280', text: '#ffffff' };
      default:
        return { bg: '#6b7280', text: '#ffffff' };
    }
  };

  const getStatusText = (status: string, isDeleted?: boolean) => {
    if (isDeleted) {
      return 'Deleted';
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Handle edit blog
  const handleEditBlog = (blog: Blog) => {
    console.log('📝 Starting to edit blog:', blog);
    console.log('📝 Original author data:', {
      author: blog.author,
      hasAuthor: !!blog.author,
      authorName: blog.author?.name,
      authorEmail: blog.author?.email,
      authorType: typeof blog.author,
    });

    setSelectedBlog(blog);
    setFormData({
      title: blog.title,
      slug: blog.slug,
      excerpt: blog.excerpt,
      content: blog.content,
      category: blog.category,
      tags: blog.tags.join(', '),
      authorName: blog.author?.name || '',
      authorEmail: blog.author?.email || '',
      readTime: blog.readTime,
      status: blog.status,
      isFeatured: blog.isFeatured,
    });
    // Reset image selection and preview when editing
    setSelectedImage(null);
    setImagePreview(blog.featuredImage || null);
    setShowEditModal(true);

    console.log('📝 Form data set for editing:', {
      authorName: blog.author?.name || '',
      authorEmail: blog.author?.email || '',
      hasAuthorName: !!blog.author?.name,
      hasAuthorEmail: !!blog.author?.email,
    });
  };

  // Handle delete blog
  const handleDeleteBlog = (blogId: string) => {
    setBlogToDelete(blogId);
    setShowDeleteModal(true);
  };

  // Confirm delete blog
  const confirmDeleteBlog = async () => {
    if (!blogToDelete) return;

    try {
      setDeleting(true);
      console.log('🗑️ Deleting blog:', blogToDelete);

      const response = await services.delete(API_ENDPOINTS.BLOG.DELETE(blogToDelete));

      console.log('✅ Delete response:', response);

      if (response.data && response.data.success) {
        setShowDeleteModal(false);
        setBlogToDelete(null);
        fetchBlogs(); // Refresh the list
      } else {
        setError(response.data?.message || 'Failed to delete blog');
      }
    } catch (err: unknown) {
      console.error('❌ Error deleting blog:', err);
      const error = err as { data?: { message?: string }; status?: number; statusText?: string };
      setError(error.data?.message || error.statusText || 'Failed to delete blog');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreBlog = async (blogId: string) => {
    try {
      setRestoring(true);
      console.log('🔄 Restoring blog with ID:', blogId);

      // Use the restore endpoint (PATCH method)
      const response = await services.put(API_ENDPOINTS.BLOG.RESTORE(blogId), {});
      console.log('✅ Restore response:', response);

      if (response.data && response.data.success) {
        console.log('✅ Blog restored successfully');
        await fetchBlogs(); // Refresh the blog list
      } else {
        console.error('❌ Restore failed:', response.data);
        setError('Failed to restore blog');
      }
    } catch (err: unknown) {
      console.error('❌ Error restoring blog:', err);
      const error = err as { data?: { message?: string }; status?: number; statusText?: string };
      setError(error.data?.message || error.statusText || 'Failed to restore blog');
    } finally {
      setRestoring(false);
    }
  };

  const toggleDropdown = (blogId: string) => {
    setOpenDropdown(openDropdown === blogId ? null : blogId);
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown && !(event.target as Element).closest('.dropdown-container')) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // Create portal for dropdown to render outside table
  useEffect(() => {
    if (openDropdown) {
      // Add body class to prevent scrolling issues
      document.body.style.overflow = 'visible';
      return () => {
        document.body.style.overflow = 'auto';
      };
    }
  }, [openDropdown]);

  const filteredBlogs = Array.isArray(blogs)
    ? blogs.filter((blog) => {
        // Filter out null/undefined blogs
        if (!blog) return false;

        // No need to filter by deleted status since we're using dedicated endpoints
        // The API already returns the correct blogs based on showDeleted state

        if (!searchTerm.trim()) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
          blog.title.toLowerCase().includes(searchLower) ||
          blog.category.toLowerCase().includes(searchLower) ||
          (blog.author?.name || '').toLowerCase().includes(searchLower) ||
          blog.tags.some((tag) => tag.toLowerCase().includes(searchLower))
        );
      })
    : [];

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalBlogs);

  // Calculate statistics based on current filter
  // Since we're using dedicated endpoints, the current blogs are already filtered
  const publishedBlogs = Array.isArray(blogs)
    ? blogs.filter((blog) => blog && blog.status === 'published')
    : [];
  const draftBlogs = Array.isArray(blogs)
    ? blogs.filter((blog) => blog && blog.status === 'draft')
    : [];

  // Use filtered blogs for display
  const displayBlogs = filteredBlogs;
  const displayTotal = totalBlogs; // Use the total from API response

  if (loading && !error) {
    return (
      <div
        className='flex items-center justify-center min-h-screen'
        style={{ backgroundColor: '#0B222C' }}
      >
        <div
          className='animate-spin rounded-full h-12 w-12 border-b-2'
          style={{ borderColor: '#095D52' }}
        ></div>
        <p className='ml-4 font-work-sans' style={{ color: '#ede8c5' }}>
          Loading blogs...
        </p>
      </div>
    );
  }

  // Remove the early return for error - we'll show the interface with error message

  return (
    <div className='p-6' style={{ backgroundColor: '#0B222C', minHeight: '100vh' }}>
      {/* Header */}
      <div className='mb-8'>
        <h1
          className='text-3xl font-bold mb-2 font-dm-serif'
          style={{
            color: '#ede8c5',
            textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
            letterSpacing: '0.5px',
          }}
        >
          Blog Management
        </h1>
        <p className='text-lg font-work-sans' style={{ color: '#ede8c5' }}>
          Manage your blog posts and content
        </p>
      </div>

      {/* Error Banner */}
      {error && (
        <div
          className='mb-6 p-4 rounded-lg border'
          style={{ backgroundColor: '#0B222C', borderColor: '#dc2626' }}
        >
          <div className='flex items-center justify-between'>
            <div className='flex items-center'>
              <svg
                className='w-5 h-5 mr-3 text-red-500'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              <p className='text-red-500 font-work-sans'>{error}</p>
            </div>
            <button
              onClick={() => {
                setError(null);
                fetchBlogs();
              }}
              className='px-4 py-2 rounded-lg transition-colors text-sm font-work-sans'
              style={{ backgroundColor: '#095D52', color: '#ede8c5' }}
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
        <div
          className='p-6 rounded-lg border shadow-lg'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
          }}
        >
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                {showDeleted ? 'Deleted Blogs' : 'Total Blogs'}
              </p>
              <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {error ? '--' : displayTotal}
              </p>
            </div>
            <div className='p-3 rounded-lg'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          className='p-6 rounded-lg border shadow-lg'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
          }}
        >
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                {showDeleted ? 'Deleted Published' : 'Published'}
              </p>
              <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {error ? '--' : publishedBlogs.length}
              </p>
            </div>
            <div className='p-3 rounded-lg'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          className='p-6 rounded-lg border shadow-lg'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
          }}
        >
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                {showDeleted ? 'Deleted Drafts' : 'Drafts'}
              </p>
              <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {error ? '--' : draftBlogs.length}
              </p>
            </div>
            <div className='p-3 rounded-lg'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Add Button */}
      <div className='flex flex-col sm:flex-row gap-4 mb-6'>
        <div className='flex-1'>
          <input
            type='text'
            placeholder='Search blogs by title, category, author, or tags...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50 font-work-sans'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              color: '#ede8c5',
            }}
          />
        </div>

        {/* Toggle for showing deleted blogs */}
        <div className='flex items-center gap-2'>
          <label className='flex items-center gap-2 cursor-pointer'>
            <input
              type='checkbox'
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className='w-4 h-4 rounded border-2'
              style={{
                backgroundColor: showDeleted ? '#bbe425' : '#0A2E32',
                borderColor: '#465705',
                accentColor: '#bbe425',
              }}
            />
            <span className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
              Show Deleted
            </span>
          </label>
        </div>

        <button
          onClick={() => {
            console.log('🔗 API Endpoint:', API_ENDPOINTS.BLOG.CREATE);
            setShowAddModal(true);
          }}
          className='px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 font-work-sans'
          style={{ backgroundColor: '#095D52', color: '#ede8c5' }}
        >
          <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
          </svg>
          Add Blog
        </button>
      </div>

      {/* Blogs Table */}
      <div
        className='rounded-xl border shadow-lg overflow-hidden'
        style={{
          backgroundColor: '#0B222C',
          borderColor: '#095D52',
          overflow: 'visible',
          boxShadow:
            '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
          backgroundImage:
            'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
        }}
      >
        <div
          className='overflow-x-auto'
          style={{ overflow: 'visible', position: 'relative', zIndex: 1 }}
        >
          <table className='w-full' style={{ position: 'relative', zIndex: 1 }}>
            <thead
              className='rounded-t-xl'
              style={{
                backgroundColor: '#095D52',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              }}
            >
              <tr>
                <th
                  className='px-8 py-6 text-left text-sm font-bold uppercase tracking-wider font-work-sans rounded-tl-xl'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Title
                </th>
                <th
                  className='px-6 py-6 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Category
                </th>
                <th
                  className='px-6 py-6 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Author
                </th>
                <th
                  className='px-6 py-6 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Status
                </th>
                <th
                  className='px-6 py-6 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Views
                </th>
                <th
                  className='px-6 py-6 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Created
                </th>
                <th
                  className='px-6 py-6 text-left text-sm font-bold uppercase tracking-wider font-work-sans rounded-tr-xl'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody
              style={{
                backgroundColor: '#0B222C',
                overflow: 'visible',
                position: 'relative',
                zIndex: 1,
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
              }}
              className='divide-y divide-gray-700'
            >
              {filteredBlogs.length === 0 ? (
                <tr>
                  <td colSpan={7} className='px-8 py-16 text-center'>
                    <div className='flex flex-col items-center space-y-4'>
                      <div
                        className='p-4 rounded-full'
                        style={{
                          backgroundColor: '#095D52',
                          boxShadow:
                            '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                          backgroundImage:
                            'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                        }}
                      >
                        <svg
                          className='w-8 h-8'
                          style={{ color: '#ede8c5' }}
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                          />
                        </svg>
                      </div>
                      <div className='space-y-2'>
                        <p
                          className='text-xl font-bold font-dm-serif'
                          style={{
                            color: '#ede8c5',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                            letterSpacing: '0.3px',
                          }}
                        >
                          {error
                            ? 'Unable to load blogs'
                            : showDeleted
                              ? 'No deleted blogs found'
                              : 'No blogs found'}
                        </p>
                        <p
                          className='text-sm font-work-sans max-w-md mx-auto leading-relaxed'
                          style={{ color: '#b8c5c2' }}
                        >
                          {error
                            ? 'There was an error loading the blogs. You can still create new blog posts using the "Add Blog" button above.'
                            : searchTerm
                              ? 'Try adjusting your search terms'
                              : showDeleted
                                ? 'No blogs have been deleted yet'
                                : 'Get started by creating your first blog post'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                displayBlogs.map((blog) => (
                  <tr
                    key={blog._id}
                    className='transition-all duration-300 hover:scale-[1.01]'
                    style={{
                      backgroundColor: '#0B222C',
                      opacity: blog.isDeleted ? 0.7 : 1,
                      overflow: 'visible',
                      position: 'relative',
                      zIndex: openDropdown === blog._id ? 999999 : 1,
                      borderBottom: '1px solid rgba(9, 93, 82, 0.2)',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2)';
                      e.currentTarget.style.borderBottom = '1px solid rgba(187, 228, 37, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#0B222C';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.borderBottom = '1px solid rgba(9, 93, 82, 0.2)';
                    }}
                  >
                    <td className='px-6 py-4'>
                      <div className='flex items-start space-x-3'>
                        {blog.featuredImage && (
                          <div className='flex-shrink-0'>
                            <img
                              src={blog.featuredImage}
                              alt={blog.title}
                              className='w-12 h-12 rounded-lg object-cover shadow-lg'
                              style={{
                                border: '2px solid rgba(9, 93, 82, 0.4)',
                                boxShadow:
                                  '0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2)',
                              }}
                            />
                          </div>
                        )}
                        <div className='flex-1 min-w-0'>
                          <div
                            className='text-base font-bold font-dm-serif mb-2 leading-tight truncate'
                            style={{
                              color: '#ede8c5',
                              textShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
                              letterSpacing: '0.3px',
                              lineHeight: '1.3',
                              maxWidth: '200px',
                            }}
                            title={blog.title}
                          >
                            {blog.title}
                          </div>
                          <div
                            className='text-sm font-work-sans leading-relaxed'
                            style={{
                              color: '#b8c5c2',
                              lineHeight: '1.5',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {blog.excerpt.length > 100
                              ? `${blog.excerpt.substring(0, 100)}...`
                              : blog.excerpt}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <span
                        className='inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold font-work-sans'
                        style={{
                          backgroundColor: '#095D52',
                          color: '#ede8c5',
                          border: '1px solid rgba(187, 228, 37, 0.3)',
                          boxShadow:
                            '0 2px 6px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                        }}
                      >
                        {blog.category}
                      </span>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='flex flex-col space-y-1'>
                        <div
                          className='text-sm font-semibold font-work-sans'
                          style={{ color: '#ede8c5' }}
                        >
                          {blog.author?.name || 'N/A'}
                        </div>
                        <div className='text-xs font-work-sans' style={{ color: '#b8c5c2' }}>
                          {blog.author?.email || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <span
                        className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
                        style={{
                          backgroundColor: getStatusColor(blog.status, blog.isDeleted).bg,
                          color: getStatusColor(blog.status, blog.isDeleted).text,
                        }}
                      >
                        {getStatusText(blog.status, blog.isDeleted)}
                      </span>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='flex items-center space-x-2'>
                        <svg
                          className='w-4 h-4'
                          style={{ color: '#095D52' }}
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                          />
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                          />
                        </svg>
                        <span
                          className='text-sm font-semibold font-work-sans'
                          style={{ color: '#ede8c5' }}
                        >
                          {blog.viewCount}
                        </span>
                      </div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='flex flex-col space-y-1'>
                        <div
                          className='text-sm font-medium font-work-sans'
                          style={{ color: '#ede8c5' }}
                        >
                          {formatDate(blog.createdAt).split(',')[0]}
                        </div>
                        <div className='text-xs font-work-sans' style={{ color: '#b8c5c2' }}>
                          {formatDate(blog.createdAt).split(',')[1]?.trim()}
                        </div>
                      </div>
                    </td>
                    <td
                      className='px-4 py-4 whitespace-nowrap text-sm font-medium'
                      style={{ position: 'relative', zIndex: 1 }}
                    >
                      <div
                        className='relative dropdown-container'
                        style={{ zIndex: openDropdown === blog._id ? 999999 : 1 }}
                      >
                        {/* Actions Dropdown Button */}
                        <button
                          onClick={() => toggleDropdown(blog._id)}
                          className='inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105 font-work-sans'
                          style={{
                            backgroundColor: '#0B222C',
                            border: '2px solid #095D52',
                            color: '#ede8c5',
                            boxShadow:
                              '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                            backgroundImage:
                              'linear-gradient(135deg, rgba(187, 228, 37, 0.05) 0%, rgba(9, 93, 82, 0.1) 50%, rgba(11, 34, 44, 0.95) 100%)',
                            zIndex: 1,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#095D52';
                            e.currentTarget.style.borderColor = '#bbe425';
                            e.currentTarget.style.boxShadow =
                              '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#0B222C';
                            e.currentTarget.style.borderColor = '#095D52';
                            e.currentTarget.style.boxShadow =
                              '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                          }}
                        >
                          <svg
                            className='w-4 h-4 mr-1'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z'
                            />
                          </svg>
                          Actions
                          <svg
                            className='w-4 h-4 ml-1'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M19 9l-7 7-7-7'
                            />
                          </svg>
                        </button>

                        {/* Dropdown Menu */}
                        {openDropdown === blog._id && (
                          <div
                            className='absolute right-0 mt-3 w-48 rounded-lg shadow-xl border-2'
                            style={{
                              backgroundColor: '#0B222C',
                              borderColor: '#095D52',
                              zIndex: 999999,
                              position: 'absolute',
                              top: 'calc(100% + 8px)',
                              right: 0,
                              minWidth: '192px',
                              boxShadow:
                                '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
                              backgroundImage:
                                'linear-gradient(135deg, rgba(187, 228, 37, 0.05) 0%, rgba(9, 93, 82, 0.1) 50%, rgba(11, 34, 44, 0.98) 100%)',
                            }}
                          >
                            <div>
                              {/* View Option */}
                              
                              <button
                                onClick={() => {
                                  fetchBlogDetails(blog._id);
                                  closeDropdown();
                                }}
                                disabled={blogLoading}
                                className='flex items-center w-full px-4 py-3 text-sm font-work-sans transition-all duration-200 hover:scale-[1.02]'
                                style={{
                                  color: '#ede8c5',
                                  backgroundColor: 'transparent',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#095D52';
                                  e.currentTarget.style.color = '#ede8c5';
                                  e.currentTarget.style.boxShadow =
                                    '0 4px 12px rgba(9, 93, 82, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#ede8c5';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                <div
                                  className='flex items-center justify-center w-8 h-8 mr-3 rounded-lg'
                                  style={{ backgroundColor: '#095D52' }}
                                >
                                  <svg
                                    className='w-4 h-4'
                                    style={{ color: '#ede8c5' }}
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                  >
                                    <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                                    />
                                    <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                                    />
                                  </svg>
                                </div>
                                <div className='flex flex-col items-start'>
                                  <span className='font-medium'>
                                    {blogLoading ? 'Loading...' : 'View Details'}
                                  </span>
                                  <span className='text-xs opacity-75'>View blog information</span>
                                </div>
                              </button>

                              {/* Edit Option (only for active blogs) */}
                              {!blog.isDeleted && (
                                <button
                                  onClick={() => {
                                    handleEditBlog(blog);
                                    closeDropdown();
                                  }}
                                  className='flex items-center w-full px-4 py-3 text-sm font-work-sans transition-all duration-200 hover:scale-[1.02]'
                                  style={{
                                    color: '#ede8c5',
                                    backgroundColor: 'transparent',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#095D52';
                                    e.currentTarget.style.color = '#ede8c5';
                                    e.currentTarget.style.boxShadow =
                                      '0 4px 12px rgba(9, 93, 82, 0.3)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#ede8c5';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                >
                                  <div
                                    className='flex items-center justify-center w-8 h-8 mr-3 rounded-lg'
                                    style={{ backgroundColor: '#095D52' }}
                                  >
                                    <svg
                                      className='w-4 h-4'
                                      style={{ color: '#ede8c5' }}
                                      fill='none'
                                      stroke='currentColor'
                                      viewBox='0 0 24 24'
                                    >
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                                      />
                                    </svg>
                                  </div>
                                  <div className='flex flex-col items-start'>
                                    <span className='font-medium'>Edit Blog</span>
                                    <span className='text-xs opacity-75'>Modify blog content</span>
                                  </div>
                                </button>
                              )}

                              {/* Delete/Restore Option */}
                              {!blog.isDeleted ? (
                                <button
                                  onClick={() => {
                                    handleDeleteBlog(blog._id);
                                    closeDropdown();
                                  }}
                                  className='flex items-center w-full px-4 py-3 text-sm font-work-sans transition-all duration-200 hover:scale-[1.02]'
                                  style={{
                                    color: '#EF4444',
                                    backgroundColor: 'transparent',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#EF4444';
                                    e.currentTarget.style.color = '#ffffff';
                                    e.currentTarget.style.boxShadow =
                                      '0 4px 12px rgba(239, 68, 68, 0.3)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#EF4444';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                >
                                  <div
                                    className='flex items-center justify-center w-8 h-8 mr-3 rounded-lg'
                                    style={{ backgroundColor: '#EF4444' }}
                                  >
                                    <svg
                                      className='w-4 h-4'
                                      style={{ color: '#ffffff' }}
                                      fill='none'
                                      stroke='currentColor'
                                      viewBox='0 0 24 24'
                                    >
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                                      />
                                    </svg>
                                  </div>
                                  <div className='flex flex-col items-start'>
                                    <span className='font-medium'>Delete Blog</span>
                                    <span className='text-xs opacity-75'>Remove permanently</span>
                                  </div>
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    handleRestoreBlog(blog._id);
                                    closeDropdown();
                                  }}
                                  disabled={restoring}
                                  className='flex items-center w-full px-4 py-3 text-sm font-work-sans transition-all duration-200 hover:scale-[1.02]'
                                  style={{
                                    color: '#10B981',
                                    backgroundColor: 'transparent',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!restoring) {
                                      e.currentTarget.style.backgroundColor = '#10B981';
                                      e.currentTarget.style.color = '#ffffff';
                                      e.currentTarget.style.boxShadow =
                                        '0 4px 12px rgba(16, 185, 129, 0.3)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!restoring) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = '#10B981';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }
                                  }}
                                >
                                  <div
                                    className='flex items-center justify-center w-8 h-8 mr-3 rounded-lg'
                                    style={{ backgroundColor: '#10B981' }}
                                  >
                                    <svg
                                      className='w-4 h-4'
                                      style={{ color: '#ffffff' }}
                                      fill='none'
                                      stroke='currentColor'
                                      viewBox='0 0 24 24'
                                    >
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                                      />
                                    </svg>
                                  </div>
                                  <div className='flex flex-col items-start'>
                                    <span className='font-medium'>
                                      {restoring ? 'Restoring...' : 'Restore Blog'}
                                    </span>
                                    <span className='text-xs opacity-75'>Recover deleted blog</span>
                                  </div>
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!error && (
        <div className='mt-6' style={{ borderTop: '1px solid #465705' }}>
          <div className='flex justify-between items-center pt-4'>
            <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
              Page {currentPage} of {totalPages} • Showing {startIndex} to {endIndex} of{' '}
              {displayTotal} {showDeleted ? 'deleted' : 'active'} blogs
            </div>

            <div className='flex space-x-2'>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1 || totalPages <= 1}
                className='flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-work-sans'
                style={{
                  backgroundColor: currentPage === 1 || totalPages <= 1 ? '#095D52' : '#0B222C',
                  border: '1px solid #bbe425',
                  color: '#ede8c5',
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1 && totalPages > 1) {
                    e.currentTarget.style.backgroundColor = '#bbe425';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 1 && totalPages > 1) {
                    e.currentTarget.style.backgroundColor = '#0B222C';
                  }
                }}
              >
                <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 19l-7-7 7-7'
                  />
                </svg>
                Previous
              </button>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages <= 1}
                className='flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-work-sans'
                style={{
                  backgroundColor:
                    currentPage === totalPages || totalPages <= 1 ? '#095D52' : '#bbe425',
                  border: '1px solid #bbe425',
                  color: '#ede8c5',
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages && totalPages > 1) {
                    e.currentTarget.style.backgroundColor = '#ede8c5';
                    e.currentTarget.style.color = '#0B222C';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== totalPages && totalPages > 1) {
                    e.currentTarget.style.backgroundColor = '#bbe425';
                    e.currentTarget.style.color = '#ede8c5';
                  }
                }}
              >
                Next
                <svg className='w-4 h-4 ml-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 5l7 7-7 7'
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Blog Modal */}
      {showAddModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div
            className='bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-lg'
            style={{
              backgroundColor: '#0B222C',
              border: '2px solid #095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div
              className='flex items-center justify-between p-6 border-b'
              style={{ borderColor: '#095D52' }}
            >
              <h3
                className='text-lg font-semibold font-dm-serif'
                style={{
                  color: '#ede8c5',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                  letterSpacing: '0.8px',
                }}
              >
                Add New Blog Post
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className='text-2xl transition-all duration-300 hover:scale-110'
                style={{ color: '#ede8c5' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#bbe425';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#ede8c5';
                }}
              >
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className='p-6 space-y-6'>
              {/* Error Display */}
              {error && (
                <div className='bg-red-900 border border-red-600 text-red-100 px-4 py-3 rounded-lg'>
                  {error}
                </div>
              )}

              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Title *
                  </label>
                  <input
                    type='text'
                    name='title'
                    value={formData.title}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Slug *
                  </label>
                  <input
                    type='text'
                    name='slug'
                    value={formData.slug}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                  Excerpt *
                </label>
                <textarea
                  name='excerpt'
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  rows={3}
                  className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                  style={{
                    backgroundColor: '#0A2E32',
                    borderColor: '#465705',
                    color: '#ede8c5',
                  }}
                />
              </div>

              <div>
                <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                  Content * (Minimum 100 characters)
                </label>

                <RichTextEditor
                  value={formData.content}
                  onChange={(content) => setFormData((prev) => ({ ...prev, content }))}
                  placeholder='Start writing your blog content here...'
                  height={300}
                />
                <p className='text-xs text-gray-400 mt-1'>
                  Current length: {formData.content.length} characters (Minimum: 100)
                </p>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Category *
                  </label>
                  <select
                    name='category'
                    value={formData.category}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  >
                    <option value=''>Select a category</option>
                    <option value='Technology'>Technology</option>
                    <option value='Business'>Business</option>
                    <option value='Finance'>Finance</option>
                    <option value='Real Estate'>Real Estate</option>
                    <option value='Investment'>Investment</option>
                    <option value='Crypto'>Crypto</option>
                    <option value='Blockchain'>Blockchain</option>
                    <option value='Web Development'>Web Development</option>
                    <option value='Programming'>Programming</option>
                    <option value='Lifestyle'>Lifestyle</option>
                    <option value='News'>News</option>
                    <option value='Tutorial'>Tutorial</option>
                    <option value='Guide'>Guide</option>
                    <option value='Review'>Review</option>
                    <option value='Opinion'>Opinion</option>
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Tags (comma-separated) *
                  </label>
                  <input
                    type='text'
                    name='tags'
                    value={formData.tags}
                    onChange={handleInputChange}
                    placeholder='technology, web development, react'
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                  <p className='text-xs text-gray-400 mt-1'>
                    Enter tags separated by commas (e.g., technology, web development, react)
                  </p>
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Author Name *
                  </label>
                  <input
                    type='text'
                    name='authorName'
                    value={formData.authorName}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Author Email *
                  </label>
                  <input
                    type='email'
                    name='authorEmail'
                    value={formData.authorEmail}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Read Time (minutes) *
                  </label>
                  <input
                    type='number'
                    name='readTime'
                    value={formData.readTime}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Status
                  </label>
                  <select
                    name='status'
                    value={formData.status}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  >
                    <option value='draft'>Draft</option>
                    <option value='published'>Published</option>
                    <option value='archived'>Archived</option>
                  </select>
                </div>

                <div className='flex items-center'>
                  <input
                    type='checkbox'
                    name='isFeatured'
                    checked={formData.isFeatured}
                    onChange={handleInputChange}
                    className='mr-2'
                  />
                  <label className='text-sm font-medium' style={{ color: '#ede8c5' }}>
                    Featured Post
                  </label>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                  Featured Image
                </label>
                <input
                  type='file'
                  accept='image/*'
                  onChange={handleImageChange}
                  className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                  style={{
                    backgroundColor: '#0A2E32',
                    borderColor: '#465705',
                    color: '#ede8c5',
                  }}
                />
                {imagePreview && (
                  <div className='mt-4'>
                    <img
                      src={imagePreview}
                      alt='Preview'
                      className='w-32 h-32 object-cover rounded-lg'
                    />
                  </div>
                )}
              </div>

              <div
                className='flex justify-end gap-4 pt-6 border-t'
                style={{ borderColor: '#095D52' }}
              >
                <button
                  type='button'
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className='px-6 py-2 rounded-lg font-work-sans transition-all duration-300 hover:scale-105'
                  style={{
                    backgroundColor: '#095D52',
                    color: '#ede8c5',
                    border: '1px solid #095D52',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = '#bbe425';
                    target.style.boxShadow =
                      '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = '#095D52';
                    target.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={submitting}
                  onClick={() => console.log('🔘 Create Blog button clicked!')}
                  className='px-6 py-2 rounded-lg font-work-sans transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed'
                  style={{
                    backgroundColor: '#bbe425',
                    color: '#0B222C',
                    border: '1px solid #bbe425',
                    boxShadow:
                      '0 4px 15px rgba(187, 228, 37, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#a3c41a';
                      target.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(163, 196, 26, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#bbe425';
                      target.style.boxShadow =
                        '0 4px 15px rgba(187, 228, 37, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }
                  }}
                >
                  {submitting ? 'Creating...' : 'Create Blog'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Blog Details Modal */}
      {showViewModal && selectedBlog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div
            className='bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-lg'
            style={{
              backgroundColor: '#0B222C',
              border: '2px solid #095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div
              className='flex items-center justify-between p-6 border-b'
              style={{ borderColor: '#095D52' }}
            >
              <h3
                className='text-lg font-semibold font-dm-serif'
                style={{
                  color: '#ede8c5',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                  letterSpacing: '0.8px',
                }}
              >
                Blog Details
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className='text-2xl transition-all duration-300 hover:scale-110'
                style={{ color: '#ede8c5' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#bbe425';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#ede8c5';
                }}
              >
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            <div className='p-6'>
              <div className='space-y-6'>
                {/* Basic Information */}
                <div>
                  <h4 className='text-lg font-semibold mb-4' style={{ color: '#ede8c5' }}>
                    Basic Information
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label
                        className='block text-sm font-medium mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Title
                      </label>
                      <p className='text-sm' style={{ color: '#ede8c5' }}>
                        {selectedBlog.title}
                      </p>
                    </div>
                    <div>
                      <label
                        className='block text-sm font-medium mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Slug
                      </label>
                      <p className='text-sm' style={{ color: '#ede8c5' }}>
                        {selectedBlog.slug}
                      </p>
                    </div>
                    <div>
                      <label
                        className='block text-sm font-medium mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Category
                      </label>
                      <p className='text-sm' style={{ color: '#ede8c5' }}>
                        {selectedBlog.category}
                      </p>
                    </div>
                    <div>
                      <label
                        className='block text-sm font-medium mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Status
                      </label>
                      <span
                        className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
                        style={{
                          backgroundColor: getStatusColor(selectedBlog.status).bg,
                          color: getStatusColor(selectedBlog.status).text,
                        }}
                      >
                        {getStatusText(selectedBlog.status)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Author Information */}
                <div>
                  <h4 className='text-lg font-semibold mb-4' style={{ color: '#ede8c5' }}>
                    Author Information
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label
                        className='block text-sm font-medium mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Author Name
                      </label>
                      <p className='text-sm' style={{ color: '#ede8c5' }}>
                        {selectedBlog.author?.name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label
                        className='block text-sm font-medium mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Author Email
                      </label>
                      <p className='text-sm' style={{ color: '#ede8c5' }}>
                        {selectedBlog.author?.email || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Content Information */}
                <div>
                  <h4 className='text-lg font-semibold mb-4' style={{ color: '#ede8c5' }}>
                    Content Information
                  </h4>
                  <div className='space-y-4'>
                    <div>
                      <label
                        className='block text-sm font-medium mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Excerpt
                      </label>
                      <p className='text-sm' style={{ color: '#ede8c5' }}>
                        {selectedBlog.excerpt}
                      </p>
                    </div>
                    <div>
                      <label
                        className='block text-sm font-medium mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Content
                      </label>
                      <div
                        className='text-sm p-4 rounded-lg max-w-none blog-content'
                        style={{
                          backgroundColor: '#0A2E32',
                          color: '#ede8c5',
                          lineHeight: '1.6',
                        }}
                        dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
                      />
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {selectedBlog.tags && selectedBlog.tags.length > 0 && (
                  <div>
                    <h4 className='text-lg font-semibold mb-4' style={{ color: '#ede8c5' }}>
                      Tags
                    </h4>
                    <div className='flex flex-wrap gap-2'>
                      {selectedBlog.tags.map((tag, index) => (
                        <span
                          key={index}
                          className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium'
                          style={{
                            backgroundColor: '#465705',
                            color: '#ede8c5',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Featured Image */}
                {selectedBlog.featuredImage && (
                  <div>
                    <h4 className='text-lg font-semibold mb-4' style={{ color: '#ede8c5' }}>
                      Featured Image
                    </h4>
                    <img
                      src={selectedBlog.featuredImage}
                      alt={selectedBlog.title}
                      className='w-full max-w-md rounded-lg'
                    />
                  </div>
                )}

                {/* Statistics */}
                <div>
                  <h4 className='text-lg font-semibold mb-4' style={{ color: '#ede8c5' }}>
                    Statistics
                  </h4>
                  <div className='grid grid-cols-2 md:grid-cols-3 gap-3'>
                    <div
                      className='text-center p-4 rounded-lg'
                      style={{ backgroundColor: '#0A2E32' }}
                    >
                      <div className='text-2xl font-bold' style={{ color: '#ede8c5' }}>
                        {selectedBlog.viewCount}
                      </div>
                      <div className='text-sm' style={{ color: '#ede8c5' }}>
                        Views
                      </div>
                    </div>
                    <div
                      className='text-center p-4 rounded-lg'
                      style={{ backgroundColor: '#0A2E32' }}
                    >
                      <div className='text-2xl font-bold' style={{ color: '#ede8c5' }}>
                        {selectedBlog.likeCount}
                      </div>
                      <div className='text-sm' style={{ color: '#ede8c5' }}>
                        Likes
                      </div>
                    </div>
                    {/* <div className='text-center p-4 rounded-lg' style={{ backgroundColor: '#0A2E32' }}>
                      <div className='text-2xl font-bold' style={{ color: '#ede8c5' }}>
                        {selectedBlog.commentCount}
                      </div>
                      <div className='text-sm' style={{ color: '#ede8c5' }}>
                        Comments
                      </div>
                    </div> */}
                    <div
                      className='text-center p-4 rounded-lg'
                      style={{ backgroundColor: '#0A2E32' }}
                    >
                      <div className='text-2xl font-bold' style={{ color: '#ede8c5' }}>
                        {selectedBlog.readTime}
                      </div>
                      <div className='text-sm' style={{ color: '#ede8c5' }}>
                        Min Read
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div>
                  <h4 className='text-lg font-semibold mb-4' style={{ color: '#ede8c5' }}>
                    Timestamps
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label
                        className='block text-sm font-medium mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Created At
                      </label>
                      <p className='text-sm' style={{ color: '#ede8c5' }}>
                        {formatDate(selectedBlog.createdAt)}
                      </p>
                    </div>
                    <div>
                      <label
                        className='block text-sm font-medium mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Updated At
                      </label>
                      <p className='text-sm' style={{ color: '#ede8c5' }}>
                        {formatDate(selectedBlog.updatedAt)}
                      </p>
                    </div>
                    {selectedBlog.publishedAt && (
                      <div>
                        <label
                          className='block text-sm font-medium mb-1'
                          style={{ color: '#ede8c5' }}
                        >
                          Published At
                        </label>
                        <p className='text-sm' style={{ color: '#ede8c5' }}>
                          {formatDate(selectedBlog.publishedAt)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='flex justify-end gap-4 p-6 border-t' style={{ borderColor: '#465705' }}>
              <button
                onClick={() => setShowViewModal(false)}
                className='px-6 py-2 rounded-lg font-work-sans transition-all duration-300 hover:scale-105'
                style={{
                  backgroundColor: '#095D52',
                  color: '#ede8c5',
                  border: '1px solid #095D52',
                  boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                  backgroundImage:
                    'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                }}
                onMouseEnter={(e) => {
                  const target = e.target as HTMLButtonElement;
                  target.style.backgroundColor = '#bbe425';
                  target.style.boxShadow =
                    '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                }}
                onMouseLeave={(e) => {
                  const target = e.target as HTMLButtonElement;
                  target.style.backgroundColor = '#095D52';
                  target.style.boxShadow =
                    '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Blog Modal */}
      {showEditModal && selectedBlog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div
            className='bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-lg'
            style={{
              backgroundColor: '#0B222C',
              border: '2px solid #095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div
              className='flex items-center justify-between p-6 border-b'
              style={{ borderColor: '#095D52' }}
            >
              <h3
                className='text-lg font-semibold font-dm-serif'
                style={{
                  color: '#ede8c5',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                  letterSpacing: '0.8px',
                }}
              >
                Edit Blog Post
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className='text-2xl transition-all duration-300 hover:scale-110'
                style={{ color: '#ede8c5' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#bbe425';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#ede8c5';
                }}
              >
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className='p-6 space-y-6'>
              {/* Error Display */}
              {error && (
                <div className='bg-red-900 border border-red-600 text-red-100 px-4 py-3 rounded-lg'>
                  {error}
                </div>
              )}

              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Title *
                  </label>
                  <input
                    type='text'
                    name='title'
                    value={formData.title}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Slug *
                  </label>
                  <input
                    type='text'
                    name='slug'
                    value={formData.slug}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                  Excerpt *
                </label>
                <textarea
                  name='excerpt'
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  rows={3}
                  className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                  style={{
                    backgroundColor: '#0A2E32',
                    borderColor: '#465705',
                    color: '#ede8c5',
                  }}
                />
              </div>

              <div>
                <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                  Content * (Minimum 100 characters)
                </label>

                <RichTextEditor
                  value={formData.content}
                  onChange={(content) => setFormData((prev) => ({ ...prev, content }))}
                  placeholder='Start writing your blog content here...'
                  height={300}
                />
                <p className='text-xs text-gray-400 mt-1'>
                  Current length: {formData.content.length} characters (Minimum: 100)
                </p>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Category *
                  </label>
                  <select
                    name='category'
                    value={formData.category}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  >
                    <option value=''>Select a category</option>
                    <option value='Technology'>Technology</option>
                    <option value='Business'>Business</option>
                    <option value='Finance'>Finance</option>
                    <option value='Real Estate'>Real Estate</option>
                    <option value='Investment'>Investment</option>
                    <option value='Crypto'>Crypto</option>
                    <option value='Blockchain'>Blockchain</option>
                    <option value='Web Development'>Web Development</option>
                    <option value='Programming'>Programming</option>
                    <option value='Lifestyle'>Lifestyle</option>
                    <option value='News'>News</option>
                    <option value='Tutorial'>Tutorial</option>
                    <option value='Guide'>Guide</option>
                    <option value='Review'>Review</option>
                    <option value='Opinion'>Opinion</option>
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Tags (comma-separated) *
                  </label>
                  <input
                    type='text'
                    name='tags'
                    value={formData.tags}
                    onChange={handleInputChange}
                    placeholder='technology, web development, react'
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                  <p className='text-xs text-gray-400 mt-1'>
                    Enter tags separated by commas (e.g., technology, web development, react)
                  </p>
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Author Name *
                  </label>
                  <input
                    type='text'
                    name='authorName'
                    value={formData.authorName}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Author Email *
                  </label>
                  <input
                    type='email'
                    name='authorEmail'
                    value={formData.authorEmail}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Read Time (minutes) *
                  </label>
                  <input
                    type='number'
                    name='readTime'
                    value={formData.readTime}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                    Status
                  </label>
                  <select
                    name='status'
                    value={formData.status}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                    style={{
                      backgroundColor: '#0A2E32',
                      borderColor: '#465705',
                      color: '#ede8c5',
                    }}
                  >
                    <option value='draft'>Draft</option>
                    <option value='published'>Published</option>
                    <option value='archived'>Archived</option>
                  </select>
                </div>

                <div className='flex items-center'>
                  <label className='flex items-center'>
                    <input
                      type='checkbox'
                      name='isFeatured'
                      checked={formData.isFeatured}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, isFeatured: e.target.checked }))
                      }
                      className='mr-2'
                    />
                    <span className='text-sm' style={{ color: '#ede8c5' }}>
                      Featured Post
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium mb-2' style={{ color: '#ede8c5' }}>
                  Featured Image
                </label>
                <input
                  type='file'
                  accept='image/*'
                  onChange={handleImageChange}
                  className='w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-opacity-50'
                  style={{
                    backgroundColor: '#0A2E32',
                    borderColor: '#465705',
                    color: '#ede8c5',
                  }}
                />
                {imagePreview && (
                  <div className='mt-4'>
                    <div className='flex items-center justify-between mb-2'>
                      <p className='text-sm' style={{ color: '#ede8c5' }}>
                        {selectedImage ? 'New image selected:' : 'Current image:'}
                      </p>
                      {selectedImage && (
                        <button
                          type='button'
                          onClick={() => {
                            setSelectedImage(null);
                            setImagePreview(selectedBlog?.featuredImage || null);
                          }}
                          className='text-xs px-2 py-1 rounded transition-colors'
                          style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
                        >
                          Clear New Image
                        </button>
                      )}
                    </div>
                    <img
                      src={imagePreview}
                      alt='Preview'
                      className='w-32 h-32 object-cover rounded-lg'
                    />
                    {selectedImage && (
                      <p className='text-xs mt-2' style={{ color: '#10b981' }}>
                        ✓ New image will replace the current one
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div
                className='flex justify-end gap-4 pt-6 border-t'
                style={{ borderColor: '#095D52' }}
              >
                <button
                  type='button'
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className='px-6 py-2 rounded-lg font-work-sans transition-all duration-300 hover:scale-105'
                  style={{
                    backgroundColor: '#095D52',
                    color: '#ede8c5',
                    border: '1px solid #095D52',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = '#bbe425';
                    target.style.boxShadow =
                      '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = '#095D52';
                    target.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={submitting}
                  className='px-6 py-2 rounded-lg font-work-sans transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed'
                  style={{
                    backgroundColor: '#bbe425',
                    color: '#0B222C',
                    border: '1px solid #bbe425',
                    boxShadow:
                      '0 4px 15px rgba(187, 228, 37, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#a3c41a';
                      target.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(163, 196, 26, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#bbe425';
                      target.style.boxShadow =
                        '0 4px 15px rgba(187, 228, 37, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }
                  }}
                >
                  {submitting ? 'Updating...' : 'Update Blog'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div
            className='bg-white rounded-lg max-w-md w-full mx-4 shadow-lg'
            style={{
              backgroundColor: '#0B222C',
              border: '2px solid #095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div className='p-6'>
              <div className='flex items-center mb-4'>
                <svg
                  className='w-8 h-8 mr-3'
                  style={{ color: '#EF4444' }}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                  />
                </svg>
                <h3
                  className='text-lg font-semibold font-dm-serif'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Delete Blog Post
                </h3>
              </div>

              <p className='text-sm mb-6 font-work-sans' style={{ color: '#ede8c5' }}>
                Are you sure you want to delete this blog post? This action cannot be undone.
              </p>

              <div className='flex justify-end gap-4'>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setBlogToDelete(null);
                  }}
                  disabled={deleting}
                  className='px-4 py-2 rounded-lg font-work-sans transition-all duration-300 hover:scale-105'
                  style={{
                    backgroundColor: '#095D52',
                    color: '#ede8c5',
                    border: '1px solid #095D52',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    if (!deleting) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#bbe425';
                      target.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!deleting) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#095D52';
                      target.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteBlog}
                  disabled={deleting}
                  className='px-4 py-2 rounded-lg font-work-sans transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed'
                  style={{
                    backgroundColor: '#EF4444',
                    color: '#ffffff',
                    border: '1px solid #EF4444',
                    boxShadow:
                      '0 4px 15px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(220, 38, 38, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    if (!deleting) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#DC2626';
                      target.style.boxShadow =
                        '0 6px 20px rgba(220, 38, 38, 0.5), 0 0 0 1px rgba(185, 28, 28, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!deleting) {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = '#EF4444';
                      target.style.boxShadow =
                        '0 4px 15px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(220, 38, 38, 0.3)';
                    }
                  }}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPage;
